import { useState, KeyboardEvent } from "react";
import { Sparkles, Send, Loader2, ChevronRight } from "lucide-react";
import { useStytchMember } from "@stytch/react/b2b";
import {
  useAskAssistantV2,
  type AssistantV2Response,
  type AskAssistantV2Request,
} from "@/lib/api/assistantV2";
import { getUserRole, PossibleRole } from "@/lib/models/auth";
import { hasAccessToFeature } from "@/lib/models/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { AnswersRenderer } from "./components/AnswersRenderer";
import {
  WarningsPanelV2,
  ErrorPanelV2,
  CannotAnswerPanel,
  ToolRequestPanel,
} from "./components/MessagesPanelV2";
import { InterpretationPanel } from "./components/InterpretationPanel";
import { ResolutionsPanel } from "./components/ResolutionsPanel";
import { DefaultsPanel } from "./components/DefaultsPanel";
import { StatsPanel, CopyableRunId } from "./components/StatsPanel";

const EXAMPLE_QUERIES: string[] = [
  "when was the last sell signal for corn?",
  "average daily corn close in 2022",
  "how many buy signals for corn between Mar 14 and Mar 25 2023?",
  "list daily corn closes for the last 5 trading days",
];

const STATUS_STYLES: Record<string, string> = {
  ok: "bg-emerald-100 text-emerald-700 border-emerald-200",
  error: "bg-red-100 text-red-700 border-red-200",
  cannot_answer: "bg-amber-100 text-amber-700 border-amber-200",
  needs_tool: "bg-sky-100 text-sky-700 border-sky-200",
};

export function AssistantV2Dashboard() {
  const { toast } = useToast();
  const [question, setQuestion] = useState<string>("");
  const [response, setResponse] = useState<AssistantV2Response | null>(null);
  const [showRaw, setShowRaw] = useState<boolean>(false);

  const { member } = useStytchMember();
  // Developer/admin-only sections (Run/Stats, Raw response). Note: this is a
  // UX gate, not a security boundary — the backend does not enforce roles on
  // assistant-v2/ask.
  const canSeeDevSections = hasAccessToFeature({ name: "Developer", requiredPackages: ["developer"] }, member);

  const askMutation = useAskAssistantV2();

  const submit = async () => {
    const trimmed = question.trim();
    if (trimmed.length === 0) return;
    if (askMutation.isLoading) return;

    const request: AskAssistantV2Request = { question: trimmed };

    try {
      const result = await askMutation.mutateAsync(request);
      if (result.success) {
        setResponse(result.data);
      } else {
        toast({
          title: "Assistant V2 request failed",
          description: result.message || "Unknown error",
          variant: "destructive",
        });
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      toast({
        title: "Assistant V2 request failed",
        description: message,
        variant: "destructive",
      });
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Cmd/Ctrl+Enter
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  const isPending = askMutation.isLoading;
  const hasResponse = response != null;

  // Once a response exists, decide what the "primary" status block is so we
  // don't double-show an empty answers state.
  const showAnswers = hasResponse && response.answers.length > 0;
  const showEmptyAnswers =
    hasResponse &&
    response.status === "ok" &&
    response.answers.length === 0;

  return (
    <div className="flex flex-col h-screen max-h-[calc(100vh-4rem)]">
      <div className="mb-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
            Assistant V2
          </h1>
          <p className="text-sm text-slate-500">
            Ask the V2 market-data agent HedgeBeacon prices, price signals, and price changes.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pr-1">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
          <Textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask a question — e.g. 'when was the last sell signal for corn?'"
            rows={3}
            className="resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 text-base"
            disabled={isPending}
          />

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-3">
              <div className="text-[11px] text-slate-500">
                Press <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono">⌘/Ctrl</kbd>
                {" + "}
                <kbd className="px-1 py-0.5 rounded bg-slate-100 border border-slate-200 font-mono">Enter</kbd>
                {" "}to send
              </div>
            </div>
            <Button
              onClick={submit}
              disabled={isPending || question.trim().length === 0}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Thinking…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Ask
                </>
              )}
            </Button>
          </div>
        </div>

        {!hasResponse && !isPending && (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-4">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
              Try one of these
            </div>
            <div className="flex flex-wrap gap-2">
              {EXAMPLE_QUERIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQuestion(q)}
                  className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-100 text-slate-700"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {hasResponse && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-slate-500">Status</span>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                  STATUS_STYLES[response.status] ?? "bg-slate-100 text-slate-600 border-slate-200"
                }`}
              >
                {response.status}
              </span>
            </div>

            {response.versions?.run_id && (
              <div className="text-xs px-1 pb-2">
                <CopyableRunId runId={response.versions.run_id} />
              </div>
            )}

            <div className="text-xs uppercase tracking-wide text-slate-500 px-1">Question</div>
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800">
              {response.question}
            </div>

            {response.interpretation && (
              <InterpretationPanel interpretation={response.interpretation} />
            )}

            {response.status === "cannot_answer" && response.cannot_answer && (
              <CannotAnswerPanel cannotAnswer={response.cannot_answer} />
            )}
            {response.status === "needs_tool" && response.tool_request && (
              <ToolRequestPanel toolRequest={response.tool_request} />
            )}
            {response.status === "error" && response.error && (
              <ErrorPanelV2
                error={response.error}
                code={canSeeDevSections ? response.code : null}
              />
            )}

            <WarningsPanelV2 warnings={response.warnings} />

            {showAnswers && (
              <div className="space-y-3 pt-1">
                <div className="text-xs uppercase tracking-wide text-slate-500 px-1">
                  Answers ({response.answers.length})
                </div>
                <AnswersRenderer answers={response.answers} />
              </div>
            )}

            {showEmptyAnswers && (
              <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500 text-center">
                No answers returned.
              </div>
            )}

            <ResolutionsPanel resolutions={response.resolutions} />
            <DefaultsPanel defaults={response.defaults} />

            {canSeeDevSections && (
              <StatsPanel stats={response.stats} versions={response.versions} />
            )}

            {canSeeDevSections && (
              <div className="rounded-lg border border-slate-200 bg-white">
                <button
                  type="button"
                  onClick={() => setShowRaw((v) => !v)}
                  className="flex items-center gap-1.5 w-full px-4 py-2.5 text-xs uppercase tracking-wide text-slate-500 hover:text-slate-700"
                >
                  <ChevronRight
                    className={`h-3.5 w-3.5 transition-transform ${showRaw ? "rotate-90" : ""}`}
                  />
                  Raw response
                </button>
                {showRaw && (
                  <pre className="px-4 pb-4 text-xs text-slate-700 overflow-x-auto whitespace-pre-wrap break-words">
                    {JSON.stringify(response, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AssistantV2Dashboard;
